import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, IconButton, Button, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Switch, FormControlLabel, Chip, useMediaQuery,
  Card, CardContent, Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, Save, Cancel, CloudUpload, Download, LocationOn } from '@mui/icons-material';
import {
  collection, getDocs, doc, setDoc, updateDoc, deleteDoc, query, orderBy, serverTimestamp,
} from '../../services/firestoreCompat';
import { db, auth } from '../../services/firebaseConfig';

interface DeliveryZone {
  id: string;             // same as postal_code
  postal_code: string;
  min_order: number;       // €; below this delivery is blocked
  delivery_fee: number;    // € charged between min_order and free_threshold
  free_threshold: number;  // €; at/above this fee is €0
  available: boolean;      // quick disable without deleting
  city?: string;
  notes?: string;
  created_at?: any;
  updated_at?: any;
  created_by?: string;
}

const EMPTY_ZONE = {
  postal_code: '',
  min_order: '',
  delivery_fee: '',
  free_threshold: '',
  available: true,
  city: '',
  notes: '',
};

const DeliveryZonesPage: React.FC = () => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [zones, setZones] = useState<DeliveryZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<DeliveryZone | null>(null);
  const [form, setForm] = useState<any>({ ...EMPTY_ZONE });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(query(collection(db, 'delivery_zones'), orderBy('postal_code', 'asc')));
      const list: DeliveryZone[] = [];
      snap.forEach(d => {
        const data = d.data();
        list.push({
          id: d.id,
          postal_code: data.postal_code || d.id,
          min_order: Number(data.min_order) || 0,
          delivery_fee: Number(data.delivery_fee) || 0,
          free_threshold: Number(data.free_threshold) || 0,
          available: data.available !== false,
          city: data.city || '',
          notes: data.notes || '',
          created_at: data.created_at,
          updated_at: data.updated_at,
          created_by: data.created_by,
        });
      });
      setZones(list);
    } catch (e: any) {
      console.error('[DELIVERY ZONES] Load error:', e);
      setError(e?.message || 'Failed to load zones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditingZone(null);
    setForm({ ...EMPTY_ZONE });
    setDialogOpen(true);
  };

  const openEdit = (zone: DeliveryZone) => {
    setEditingZone(zone);
    setForm({
      postal_code: zone.postal_code,
      min_order: String(zone.min_order ?? ''),
      delivery_fee: String(zone.delivery_fee ?? ''),
      free_threshold: String(zone.free_threshold ?? ''),
      available: zone.available,
      city: zone.city || '',
      notes: zone.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const zip = (form.postal_code || '').trim();
    if (!zip) { setError('Postal code is required'); return; }
    const minOrder = parseFloat(form.min_order);
    const fee = parseFloat(form.delivery_fee);
    const freeThreshold = parseFloat(form.free_threshold);
    if (isNaN(minOrder) || minOrder < 0) { setError('Minimum order must be a non-negative number'); return; }
    if (isNaN(fee) || fee < 0) { setError('Delivery fee must be a non-negative number'); return; }
    if (isNaN(freeThreshold) || freeThreshold < 0) { setError('Free-delivery threshold must be a non-negative number'); return; }
    if (freeThreshold > 0 && freeThreshold < minOrder) {
      setError('Free-delivery threshold cannot be lower than the minimum order');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload: any = {
        postal_code: zip,
        min_order: minOrder,
        delivery_fee: fee,
        free_threshold: freeThreshold,
        available: form.available !== false,
        city: (form.city || '').trim(),
        notes: (form.notes || '').trim(),
        updated_at: serverTimestamp(),
        updated_by: auth.currentUser?.email || '',
      };
      if (!editingZone) {
        payload.created_at = serverTimestamp();
        payload.created_by = auth.currentUser?.email || '';
      }
      await setDoc(doc(db, 'delivery_zones', zip), payload, { merge: true });
      setDialogOpen(false);
      setEditingZone(null);
      await load();
    } catch (e: any) {
      console.error('[DELIVERY ZONES] Save error:', e);
      setError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (zone: DeliveryZone) => {
    if (!window.confirm(`Delete delivery zone for ZIP ${zone.postal_code}?`)) return;
    try {
      await deleteDoc(doc(db, 'delivery_zones', zone.id));
      await load();
    } catch (e: any) {
      setError(e?.message || 'Delete failed');
    }
  };

  const toggleAvailable = async (zone: DeliveryZone) => {
    const next = !zone.available;
    setZones(prev => prev.map(z => z.id === zone.id ? { ...z, available: next } : z));
    try {
      await updateDoc(doc(db, 'delivery_zones', zone.id), {
        available: next,
        updated_at: serverTimestamp(),
        updated_by: auth.currentUser?.email || '',
      });
    } catch (e: any) {
      setZones(prev => prev.map(z => z.id === zone.id ? { ...z, available: zone.available } : z));
      setError(e?.message || 'Toggle failed');
    }
  };

  /**
   * CSV import — one row per ZIP. Format (header line required):
   *   postal_code,min_order,delivery_fee,free_threshold,available,city,notes
   * Boolean 'available' accepts: true/false/1/0/yes/no (default true).
   */
  const handleCsvImport = async (file: File) => {
    setImporting(true);
    setError(null);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) throw new Error('CSV must have a header row and at least one data row');
      const header = lines[0].split(',').map(h => h.trim().toLowerCase());
      const idx = (name: string) => header.indexOf(name);
      const iZip = idx('postal_code');
      const iMin = idx('min_order');
      const iFee = idx('delivery_fee');
      const iFree = idx('free_threshold');
      const iAvail = idx('available');
      const iCity = idx('city');
      const iNotes = idx('notes');
      if (iZip < 0 || iMin < 0 || iFee < 0 || iFree < 0) {
        throw new Error('CSV header must include: postal_code, min_order, delivery_fee, free_threshold');
      }
      let imported = 0;
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map(c => c.trim());
        const zip = cols[iZip];
        if (!zip) continue;
        const payload: any = {
          postal_code: zip,
          min_order: parseFloat(cols[iMin]) || 0,
          delivery_fee: parseFloat(cols[iFee]) || 0,
          free_threshold: parseFloat(cols[iFree]) || 0,
          available: iAvail < 0 ? true : /^(true|1|yes|y)$/i.test(cols[iAvail] || ''),
          city: iCity >= 0 ? cols[iCity] || '' : '',
          notes: iNotes >= 0 ? cols[iNotes] || '' : '',
          updated_at: serverTimestamp(),
          updated_by: auth.currentUser?.email || '',
          created_at: serverTimestamp(),
          created_by: auth.currentUser?.email || '',
        };
        await setDoc(doc(db, 'delivery_zones', zip), payload, { merge: true });
        imported++;
      }
      await load();
      window.alert(`Imported ${imported} zones.`);
    } catch (e: any) {
      setError(e?.message || 'CSV import failed');
    } finally {
      setImporting(false);
    }
  };

  const exportCsv = () => {
    const header = 'postal_code,min_order,delivery_fee,free_threshold,available,city,notes';
    const rows = zones.map(z => [
      z.postal_code,
      z.min_order,
      z.delivery_fee,
      z.free_threshold,
      z.available ? 'true' : 'false',
      (z.city || '').replace(/,/g, ' '),
      (z.notes || '').replace(/,/g, ' '),
    ].join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `delivery_zones_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = zones.filter(z => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return z.postal_code.toLowerCase().includes(q) || (z.city || '').toLowerCase().includes(q);
  });

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 3, gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#e0e0e0', fontFamily: '"Poppins"', display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOn sx={{ color: '#00C896' }} /> Delivery Zones
            </Typography>
            <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem', mt: 0.5 }}>
              Per-ZIP rules: minimum order, delivery fee, free-delivery threshold.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<Download />} onClick={exportCsv} size="small">Export CSV</Button>
            <Button variant="outlined" startIcon={<CloudUpload />} component="label" disabled={importing} size="small">
              {importing ? 'Importing…' : 'Import CSV'}
              <input hidden type="file" accept=".csv,text/csv" onChange={(e) => {
                const f = e.target.files?.[0]; if (f) handleCsvImport(f); e.currentTarget.value = '';
              }} />
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={openAdd}
              sx={{ backgroundColor: '#00C896', color: '#1a1d2e', fontWeight: 700, '&:hover': { backgroundColor: '#7dd108' } }}>
              Add zone
            </Button>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search ZIP or city…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ maxWidth: 360, '& .MuiOutlinedInput-root': { backgroundColor: '#1e2139' } }}
            fullWidth
          />
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
        ) : filtered.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#1e2139' }}>
            <Typography sx={{ color: '#9ca3af' }}>
              No delivery zones yet. Add one per postal code the restaurant delivers to, or use the CSV import to add them in bulk.
            </Typography>
          </Paper>
        ) : isMobile ? (
          /* Mobile: card list */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {filtered.map((zone) => (
              <Card key={zone.id} sx={{ backgroundColor: '#1e2139', border: '1px solid #2a2d3e' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontWeight: 700, color: '#00C896', fontSize: '1.1rem' }}>
                      {zone.postal_code} {zone.city && <span style={{ color: '#9ca3af', fontSize: '0.85rem', fontWeight: 500 }}>· {zone.city}</span>}
                    </Typography>
                    <Chip label={zone.available ? 'Active' : 'Off'} size="small"
                      sx={{ backgroundColor: zone.available ? '#4CAF50' : '#666', color: '#fff', fontWeight: 600 }} />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 1.5 }}>
                    <Box><Typography sx={{ color: '#9ca3af', fontSize: '0.65rem', textTransform: 'uppercase' }}>Min</Typography>
                      <Typography sx={{ color: '#e0e0e0', fontWeight: 600 }}>€{zone.min_order.toFixed(2)}</Typography></Box>
                    <Box><Typography sx={{ color: '#9ca3af', fontSize: '0.65rem', textTransform: 'uppercase' }}>Fee</Typography>
                      <Typography sx={{ color: '#e0e0e0', fontWeight: 600 }}>€{zone.delivery_fee.toFixed(2)}</Typography></Box>
                    <Box><Typography sx={{ color: '#9ca3af', fontSize: '0.65rem', textTransform: 'uppercase' }}>Free at</Typography>
                      <Typography sx={{ color: '#e0e0e0', fontWeight: 600 }}>€{zone.free_threshold.toFixed(2)}</Typography></Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" onClick={() => toggleAvailable(zone)} sx={{ flex: 1 }}>
                      {zone.available ? 'Disable' : 'Enable'}
                    </Button>
                    <IconButton size="small" onClick={() => openEdit(zone)}><Edit /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(zone)} sx={{ color: '#d32f2f' }}><Delete /></IconButton>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ backgroundColor: '#1e2139', border: '1px solid #2a2d3e' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#252838' }}>
                  <TableCell sx={{ color: '#e0e0e0', fontWeight: 700, fontFamily: '"Poppins"' }}>ZIP</TableCell>
                  <TableCell sx={{ color: '#e0e0e0', fontWeight: 700, fontFamily: '"Poppins"' }}>City</TableCell>
                  <TableCell sx={{ color: '#e0e0e0', fontWeight: 700, fontFamily: '"Poppins"' }} align="right">Min order</TableCell>
                  <TableCell sx={{ color: '#e0e0e0', fontWeight: 700, fontFamily: '"Poppins"' }} align="right">Delivery fee</TableCell>
                  <TableCell sx={{ color: '#e0e0e0', fontWeight: 700, fontFamily: '"Poppins"' }} align="right">Free from</TableCell>
                  <TableCell sx={{ color: '#e0e0e0', fontWeight: 700, fontFamily: '"Poppins"' }}>Status</TableCell>
                  <TableCell sx={{ color: '#e0e0e0', fontWeight: 700, fontFamily: '"Poppins"' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((zone) => (
                  <TableRow key={zone.id} hover sx={{ '&:hover': { backgroundColor: '#252838' } }}>
                    <TableCell sx={{ color: '#00C896', fontWeight: 700, fontFamily: '"Poppins"' }}>{zone.postal_code}</TableCell>
                    <TableCell sx={{ color: '#9ca3af' }}>{zone.city || '—'}</TableCell>
                    <TableCell sx={{ color: '#e0e0e0' }} align="right">€{zone.min_order.toFixed(2)}</TableCell>
                    <TableCell sx={{ color: '#e0e0e0' }} align="right">€{zone.delivery_fee.toFixed(2)}</TableCell>
                    <TableCell sx={{ color: '#e0e0e0' }} align="right">€{zone.free_threshold.toFixed(2)}</TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={<Switch checked={zone.available} onChange={() => toggleAvailable(zone)} size="small" />}
                        label={<span style={{ color: zone.available ? '#00C896' : '#9ca3af', fontSize: '0.8rem', fontWeight: 600 }}>
                          {zone.available ? 'Active' : 'Off'}
                        </span>}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(zone)}><Edit /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDelete(zone)} sx={{ color: '#d32f2f' }}><Delete /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add/Edit dialog */}
        <Dialog open={dialogOpen} onClose={() => { if (!saving) { setDialogOpen(false); setEditingZone(null); } }} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontFamily: '"Poppins"', fontWeight: 700 }}>
            {editingZone ? `Edit zone ${editingZone.postal_code}` : 'Add delivery zone'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Postal code *" value={form.postal_code}
                onChange={(e) => setForm({ ...form, postal_code: e.target.value.replace(/\D/g, '') })}
                disabled={!!editingZone}
                helperText={editingZone ? 'Postal code is the zone ID — cannot be changed.' : 'e.g. 50354'}
                fullWidth
              />
              <TextField
                label="City (optional)" value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="Hürth"
                fullWidth
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                <TextField
                  label="Min. order (€) *" value={form.min_order}
                  onChange={(e) => setForm({ ...form, min_order: e.target.value })}
                  type="number" inputProps={{ step: '0.01', min: 0 }}
                  helperText="Below this → no delivery"
                />
                <TextField
                  label="Delivery fee (€) *" value={form.delivery_fee}
                  onChange={(e) => setForm({ ...form, delivery_fee: e.target.value })}
                  type="number" inputProps={{ step: '0.01', min: 0 }}
                  helperText="Charged between min and free"
                />
                <TextField
                  label="Free from (€) *" value={form.free_threshold}
                  onChange={(e) => setForm({ ...form, free_threshold: e.target.value })}
                  type="number" inputProps={{ step: '0.01', min: 0 }}
                  helperText="At/above → free delivery"
                />
              </Box>
              <TextField
                label="Internal notes (optional)" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                multiline rows={2} fullWidth
              />
              <FormControlLabel
                control={<Switch checked={!!form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} />}
                label="Active — delivery allowed to this ZIP"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setDialogOpen(false); setEditingZone(null); }} startIcon={<Cancel />} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <Save />}
              disabled={saving}
              sx={{ backgroundColor: '#00C896', color: '#1a1d2e', fontWeight: 700 }}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default DeliveryZonesPage;
